import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from '../../components/toast';
import { useLoadingState } from '../../hooks/useLoadingState';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import { ExpenseStatsCards, ExpenseFilters, ExpenseTable, ExpenseForm } from '../../components/expense';
import { expenseValidationSchema } from '../../utils/expenseValidation';
import {
  getInitialExpenseFormData,
  getExpenseFormDataFromRecord,
  formatExpenseDataForSubmission,
} from '../../utils/expenseUtils';
import {
  getExpenses,
  getExpenseDetails,
  createExpense,
  updateExpense,
  deleteExpense,
  getMoneyAccounts,
} from '../../services/api';
import { getUserGroups } from '../../utils';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

const allowedGroups = ['admin', 'hr'];

export default function ExpenseManagementPage() {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [currencyFilter, setCurrencyFilter] = useState('All');
  const [moneyAccountFilter, setMoneyAccountFilter] = useState('All');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    preset: 'All',
  });
  const [stats, setStats] = useState({ total_expenses: 0, total_amount: 0, categories_count: 0 });
  const [filterOptions, setFilterOptions] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [moneyAccounts, setMoneyAccounts] = useState([]);

  const [formData, setFormData] = useState(getInitialExpenseFormData());
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const loadMoneyAccounts = useCallback(async () => {
    try {
      const res = await getMoneyAccounts();
      setMoneyAccounts(res.data.results || []);
    } catch (error) {
      console.error('Failed to load money accounts:', error);
      setMoneyAccounts([]);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    await withLoading(async () => {
      const res = await getExpenses(
        page + 1,
        rowsPerPage,
        searchTerm,
        categoryFilter,
        currencyFilter,
        dateRange.startDate,
        dateRange.endDate,
        moneyAccountFilter
      );
      const data = res.data || {};
      setExpenses(data.results || data.expenses || []);
      setTotalCount(data.count ?? (data.results ? data.results.length : data.expenses?.length || 0));
      if (data.stats) {
        setStats({
          total_expenses: data.stats.total_expenses || 0,
          total_amount: data.stats.total_amount || 0,
          categories_count: Array.isArray(data.stats.category_breakdown) ? data.stats.category_breakdown.length : 0,
        });
      }
      // Update filter options if included in response
      if (data.filter_options) {
        setFilterOptions(data.filter_options);
      }
    });
  }, [
    page,
    rowsPerPage,
    searchTerm,
    categoryFilter,
    currencyFilter,
    dateRange.startDate,
    dateRange.endDate,
    moneyAccountFilter,
    withLoading,
  ]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    loadMoneyAccounts();
  }, [loadMoneyAccounts]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (mode, expense = null) => {
    setDialogMode(mode);
    setSelectedExpense(expense);
    if ((mode === 'edit' || mode === 'view') && expense) {
      try {
        const { data } = await getExpenseDetails(expense.id);
        const e = data.expense || data;
        setFormData(getExpenseFormDataFromRecord(e));
      } catch (_) {
        setFormData(getExpenseFormDataFromRecord(expense));
      }
    } else {
      setFormData(getInitialExpenseFormData());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExpense(null);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = async () => {
    try {
      await expenseValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const payload = formatExpenseDataForSubmission(formData);
      if (dialogMode === 'add') {
        await createExpense(payload);
        toast.success('Expense created');
      } else {
        await updateExpense(selectedExpense.id, payload);
        toast.success('Expense updated');
      }
      handleCloseDialog();
      loadExpenses();
      loadMoneyAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save expense');
    }
  };

  const handleDeleteExpense = (expense) => {
    setExpenseToDelete(expense);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (expenseToDelete) {
      try {
        await deleteExpense(expenseToDelete.id);
        toast.success('Expense deleted successfully');
        setOpenDeleteDialog(false);
        setExpenseToDelete(null);
        loadExpenses();
        loadMoneyAccounts();
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to delete expense');
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setExpenseToDelete(null);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPage(0);
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Expense Management</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Expense management"
            description="Track reimbursable employee expenses, monitor approval status, and record payouts against your money accounts."
            actions={
              <LightTooltip title="Log a new expense claim for an employee.">
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                  Add expense
                </Button>
              </LightTooltip>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <ExpenseStatsCards stats={stats} isLoading={isLoading} />
        </Grid>

        <ExpenseFilters
          searchTerm={searchTerm}
          categoryFilter={categoryFilter}
          currencyFilter={currencyFilter}
          moneyAccountFilter={moneyAccountFilter}
          dateRange={dateRange}
          filterOptions={filterOptions}
          moneyAccounts={moneyAccounts}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setPage(0);
          }}
          onCategoryFilterChange={(value) => {
            setCategoryFilter(value);
            setPage(0);
          }}
          onCurrencyFilterChange={(value) => {
            setCurrencyFilter(value);
            setPage(0);
          }}
          onMoneyAccountFilterChange={(value) => {
            setMoneyAccountFilter(value);
            setPage(0);
          }}
          onDateRangeChange={handleDateRangeChange}
          onClearFilters={() => {
            setSearchTerm('');
            setCategoryFilter('All');
            setCurrencyFilter('All');
            setMoneyAccountFilter('All');
            setDateRange({
              startDate: null,
              endDate: null,
              preset: 'All',
            });
            setPage(0);
          }}
        />

        <ExpenseTable
          expenses={expenses}
          isLoading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onView={(expense) => handleOpenDialog('view', expense)}
          onEdit={(expense) => handleOpenDialog('edit', expense)}
          onDelete={handleDeleteExpense}
        />
      </Grid>

      <ExpenseForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        moneyAccounts={moneyAccounts}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the expense"
        itemName={expenseToDelete?.name}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete Expense"
      />
    </>
  );
}
